import {createAdvancedSearchDancesQueryString} from 'services/utils.js'
import baseApi, {
    transformResponseFromData,
    provideTag,
} from 'store/slices/api/baseApi'

const danceApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        getDances: build.query({
            query: (query) => ({url: `dances?${query}`, method: 'GET'}),
            transformResponse: transformResponseFromData,
            providesTags: () => provideTag('Dance'),
        }),
        getDanceWithRelevantMedia: build.query({
            query: ({danceId, filter}) => ({
                url: `dances/media/${danceId}?filter=${filter}`,
                method: 'GET',
            }),
            providesTags: () => provideTag('Dance'),
        }),
        getAllMediaRequests: build.query({
            query: () => ({
                url: `dances/allMediaRequests`,
                method: 'GET',
            }),
        }),
        getDancesByAdvancedSearch: build.query({
            query: (query) => ({
                url: `dances/getDancesByAdvancedSearch?${query}`,
                method: 'GET',
            }),
            transformResponse: transformResponseFromData,
            providesTags: () => provideTag('Dance'),
        }),
        getDance: build.query({
            query: (danceId) => ({
                url: `dances/${danceId}`,
                method: 'GET',
            }),
            transformResponse: transformResponseFromData,
            providesTags: (result) => provideTag('Dance', result),
        }),
        getAdvancedSearchDances: build.query({
            query: (params) => {
                const query = createAdvancedSearchDancesQueryString(params)
                return {
                    url: `dances/advancedSearchDances${
                        query ? '?' + query : ''
                    }`,
                    method: 'GET',
                }
            },
        }),
        getSearchDances: build.query({
            query: (searchValue) => {
                return {
                    url: `dances/searchDances/?search=${searchValue}`,
                    method: 'GET',
                }
            },
            transformResponse: transformResponseFromData,
            invalidatesTags: (result) => provideTag('Dance', result),
        }),
        getLastDances: build.query({
            query: () => ({url: 'dances/lastUpdateDances', method: 'GET'}),
            transformResponse: transformResponseFromData,
            providesTags: () => provideTag('Dance'),
        }),
        getUserDances: build.query({
            query: (body) => {
                const {filter = '', choreographerId} = body ?? {}
                let baseUrl = 'dances/userDances'
                if (choreographerId) {
                    baseUrl += `/${choreographerId}`
                }
                if (filter) {
                    baseUrl += `${choreographerId ? '?' : '/?'}${filter}`
                }
                return {url: baseUrl, method: 'GET'}
            },
            transformResponse: transformResponseFromData,
            providesTags: () => provideTag('Dance'),
        }),
        saveDance: build.mutation({
            query: (dance) => {
                return {
                    url: `dances/createDance`,
                    method: 'POST',
                    body: dance,
                }
            },
            transformResponse: transformResponseFromData,
            invalidatesTags: (result) => provideTag('Dance', result),
        }),
        addMedia: build.mutation({
            query: ({danceId, body}) => {
                return {
                    url: `dances/media/${danceId}`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: () => ['Dance', 'Media'],
        }),
        updateMainMedia: build.mutation({
            query: ({danceId, mediaId}) => {
                return {
                    url: `dances/mainMedia/${danceId}/${mediaId}`,
                    method: 'PUT',
                }
            },
            invalidatesTags: () => ['Dance', 'Media'],
        }),
        createDanceMedia: build.mutation({
            query: ({mediaData, dance}) => {
                return {
                    url: `dances/createDancesMedia/${dance._id}`,
                    method: 'PUT',
                    body: {mediaData, dance},
                }
            },
            invalidatesTags: () => ['Dance', 'Media'],
        }),
        danceFavoriteMedia: build.mutation({
            query: ({dance, media}) => {
                return {
                    url: `dances/danceFavoriteMedia`,
                    method: 'PUT',
                    body: {dance, media},
                }
            },
            transformResponse: transformResponseFromData,
            invalidatesTags: () => provideTag('Dance', 'Media'),
        }),
        cancelDeleteRequest: build.mutation({
            query: ({dance, media}) => {
                return {
                    url: `dances/cancelDeleteRequest`,
                    method: 'PUT',
                    body: {dance, media},
                }
            },
            transformResponse: transformResponseFromData,
            invalidatesTags: () => provideTag('Dance', 'Media'),
        }),
        deleteDanceMedia: build.mutation({
            query: ({dance, media}) => {
                return {
                    url: `dances/deleteDanceMedia`,
                    method: 'DELETE',
                    body: {dance, media},
                }
            },
            invalidatesTags: () => ['Dance', 'Media'],
        }),
    }),
    overrideExisting: false,
})

export default danceApi

export const {
    useGetDanceQuery,
    useGetDancesQuery,
    useGetAllMediaRequestsQuery,
    useGetAdvancedSearchDancesQuery,
    useGetDanceWithRelevantMediaQuery,
    useGetDancesByAdvancedSearchQuery,
    useGetSearchDancesQuery,
    useGetUserDancesQuery,
    useCreateDanceMediaMutation,
    useDanceFavoriteMediaMutation,
    useCancelDeleteRequestMutation,
    useDeleteDanceMediaMutation,
    useUpdateMainMediaMutation,
    useSaveDanceMutation,
    useGetLastDancesQuery,
    useAddMediaMutation,
} = danceApi
